import apiService from './api.js';

// User Service class
class UserService {
  // Get all users (Admin only)
  async getAllUsers() {
    try {
      const response = await apiService.get('/users');
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get approved users only
  async getApprovedUsers(params = {}, signal = null) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('approved', 'true');
      
      if (params.role && params.role !== 'all') {
        queryParams.append('role', params.role);
      }
      if (params.search) {
        queryParams.append('search', params.search);
      }
      // Add minimal parameter to exclude heavy fields for list view
      if (params.minimal !== undefined) {
        queryParams.append('minimal', params.minimal ? 'true' : 'false');
      } else {
        // Default to minimal mode for better performance
        queryParams.append('minimal', 'true');
      }
      
      const options = signal ? { signal } : {};
      const response = await apiService.get(`/users?${queryParams.toString()}`, options);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get user by ID
  async getUserById(id) {
    try {
      const response = await apiService.get(`/users/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Create new user
  async createUser(userData) {
    try {
      const response = await apiService.post('/users', userData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Update user
  async updateUser(id, userData) {
    try {
      const response = await apiService.put(`/users/${id}`, userData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Delete user
  async deleteUser(id) {
    try {
      const response = await apiService.delete(`/users/${id}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Bulk delete users
  async bulkDeleteUsers(userIds) {
    try {
      const response = await apiService.post('/users/bulk-delete', { userIds });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Change user status
  async changeUserStatus(id, isActive) {
    try {
      const response = await apiService.patch(`/users/${id}/status`, { isActive });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Change user role
  async changeUserRole(id, role) {
    try {
      const response = await apiService.patch(`/users/${id}/role`, { role });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get distinct company names for application filter dropdown
  async getFilterCompanies(signal = null) {
    try {
      const options = signal ? { signal } : {};
      const response = await apiService.get('/users/filter/companies', options);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get users by role
  async getUsersByRole(role, signal = null) {
    try {
      const options = signal ? { signal } : {};
      const response = await apiService.get(`/users/role/${role}`, options);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Search users
  async searchUsers(query) {
    try {
      const response = await apiService.get(`/users/search/${query}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get semi-agents for an agent
  async getSemiAgents(agentId) {
    try {
      const response = await apiService.get(`/users/${agentId}/semi-agents`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Assign semi-agent to agent
  async assignSemiAgent(agentId, semiAgentId) {
    try {
      const response = await apiService.post(`/users/${agentId}/assign-semi-agent`, { semiAgentId });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Unassign semi-agent from agent
  async unassignSemiAgent(agentId, semiAgentId) {
    try {
      const response = await apiService.post(`/users/${agentId}/unassign-semi-agent`, { semiAgentId });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get all available agents
  async getAvailableAgents() {
    try {
      const response = await apiService.get('/users/available-agents');
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get all available semi-agents
  async getAvailableSemiAgents(agentId = null) {
    try {
      const url = agentId 
        ? `/users/available-semi-agents?agentId=${agentId}`
        : '/users/available-semi-agents';
      const response = await apiService.get(url);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Profile-related methods
  async getProfile() {
    try {
      const response = await apiService.get('/auth/profile');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateProfile(profileData) {
    try {
      const response = await apiService.put('/auth/profile', profileData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async changePassword(passwordData) {
    try {
      const response = await apiService.put('/auth/change-password', passwordData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async uploadAvatar(file) {
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await apiService.post('/auth/upload-avatar', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async uploadLogo(file) {
    try {
      const formData = new FormData();
      formData.append('logo', file);
      
      const response = await apiService.post('/auth/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getMyTransactions() {
    try {
      const response = await apiService.get('/applications/transactions/my');
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get users for email communication system
  async getEmailCommunicationUsers() {
    try {
      const response = await apiService.get('/users/email-communication/users');
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get companies grouped by companyName for email communication
  async getEmailCommunicationCompanies() {
    try {
      const response = await apiService.get('/users/email-communication/companies');
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Get my referrals (for Agents)
  async getMyReferrals() {
    try {
      const response = await apiService.get('/users/my/referrals');
      return response;
    } catch (error) {
      throw error;
    }
  }
}

// Create and export user service instance
const userService = new UserService();
export { userService }; 